import type { Options as IntlMessageFormatOptions } from "intl-messageformat";
import type { PrimitiveType } from "react-intl";
import { useIntl } from "react-intl";
import { TranslationItem } from "../models/commonModel";

/**
 * Thin wrapper around `useIntl().formatMessage`. Optional `values` is required for ICU placeholders
 * in react-intl v7 (e.g. `{0}` in message definitions).
 *
 * Use `formatHtmlMessage` when translated copy contains HTML tags (`<p>`, `<br/>`, etc.) so FormatJS
 * does not treat them as ICU rich-text tags (which forces fallback to defaultMessage).
 */
export default function useLocalisation(): {
  formatMessage: (
    translationItem: TranslationItem,
    values?: Record<string, PrimitiveType | undefined>
  ) => string;
  formatHtmlMessage: (
    translationItem: TranslationItem,
    values?: Record<string, PrimitiveType | undefined>
  ) => string;
} {
  const intl = useIntl();
  const toDescriptor = (translationItem: TranslationItem) => ({
    defaultMessage: translationItem.defaultMessage,
    description: translationItem.description,
    id: translationItem.id,
  });

  const htmlFormatOptions: IntlMessageFormatOptions = { ignoreTag: true };

  const formatMessage = (
    translationItem: TranslationItem,
    values?: Record<string, PrimitiveType | undefined>
  ): string => {
    return intl.formatMessage(toDescriptor(translationItem), values);
  };

  const formatHtmlMessage = (
    translationItem: TranslationItem,
    values?: Record<string, PrimitiveType | undefined>
  ): string => {
    return intl.formatMessage(
      toDescriptor(translationItem),
      values,
      htmlFormatOptions
    );
  };

  return { formatMessage, formatHtmlMessage };
}
